import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  NavigationMenu, 
  NavigationMenuContent, 
  NavigationMenuItem, 
  NavigationMenuLink, 
  NavigationMenuList, 
  NavigationMenuTrigger, 
  navigationMenuTriggerStyle 
} from './ui/navigation-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, BrainCircuit, Rocket, TrendingUp, Users, Phone, Briefcase } from 'lucide-react';
import logoPrimary from '../assets/images/LOGO_PRIMARY.png';
import { cn } from '../lib/utils';

interface HeaderProps {
  onGetStarted?: () => void;
}

export function Header({ onGetStarted }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isTransparent = !scrolled;

  const headerClasses = cn(
    "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b",
    isTransparent 
      ? "bg-transparent border-transparent py-5" 
      : "bg-white/95 backdrop-blur-md border-slate-200/50 shadow-sm py-3"
  );

  const logoClasses = cn(
    "h-14 w-auto transition-all duration-300",
    isTransparent ? "brightness-0 invert" : ""
  );

  const navTriggerClasses = cn(
    "bg-transparent text-lg",
    isTransparent 
      ? "text-white hover:bg-white/10 hover:text-white focus:text-white data-[state=open]:bg-white/10" 
      : "text-slate-900 hover:bg-slate-100/50 hover:text-blue-600 focus:text-blue-600 data-[state=open]:bg-slate-100/50"
  );

  const navLinkClasses = cn(
    "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-lg font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50",
    "bg-transparent",
    isTransparent
      ? "text-white hover:bg-white/10 hover:text-white focus:text-white"
      : "text-slate-900 hover:bg-slate-100/50 hover:text-blue-600 focus:text-blue-600"
  );

  const mobileTriggerClasses = cn(
    "md:hidden",
    isTransparent 
      ? "text-white hover:bg-white/10" 
      : "text-slate-900 hover:bg-slate-100"
  );

  return (
    <header className={headerClasses}>
      <div className="container mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0 z-50">
          <img 
            src={logoPrimary} 
            alt="Brainsie" 
            className={logoClasses} 
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:block">
          <NavigationMenu>
            <NavigationMenuList>
              
              {/* Services Dropdown */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className={navTriggerClasses}>
                  Services
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid gap-3 p-6 w-[400px] md:w-[500px] lg:w-[600px] lg:grid-cols-[.75fr_1fr]">
                    <li className="row-span-3">
                      <NavigationMenuLink asChild>
                        <Link
                          className="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-b from-[#00A8E8]/10 to-[#00A8E8]/30 p-6 no-underline outline-none focus:shadow-md"
                          to="/consulting"
                        >
                          <BrainCircuit className="h-6 w-6 text-[#00A8E8]" />
                          <div className="mb-2 mt-4 text-lg font-medium text-[#052A44]">
                            Enterprise AI
                          </div>
                          <p className="text-sm leading-tight text-[#052A44]/70">
                            Strategic AI implementation and digital transformation for large enterprises.
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/consulting"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 focus:bg-slate-100"
                        >
                          <div className="flex items-center gap-2 text-sm font-medium leading-none text-slate-900">
                            <BrainCircuit className="h-4 w-4 text-[#00A8E8]" />
                            Enterprise AI Strategy
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-slate-500">
                            Roadmap, Governance, and Execution.
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link
                          to="/small-business"
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 focus:bg-slate-100"
                        >
                          <div className="flex items-center gap-2 text-sm font-medium leading-none text-slate-900">
                            <TrendingUp className="h-4 w-4 text-[#00A8E8]" />
                            Small Business Solutions
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-slate-500">
                            Websites, SEO, and Growth Tools.
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              {/* Platform Link */}
              <NavigationMenuItem>
                <Link to="/platform">
                  <NavigationMenuLink className={navLinkClasses}>
                    Platform
                  </NavigationMenuLink>
                </Link>
              </NavigationMenuItem>

              {/* Company Dropdown */}
              <NavigationMenuItem>
                <NavigationMenuTrigger className={navTriggerClasses}>
                  Company
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                    {[
                      { title: "About Us", href: "/about", icon: Users, desc: "Our story and leadership." },
                      { title: "Success Stories", href: "/case-studies", icon: Briefcase, desc: "See our impact." },
                      { title: "Contact", href: "/contact", icon: Phone, desc: "Get in touch with us." },
                    ].map((item) => (
                      <li key={item.title}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={item.href}
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-slate-100 focus:bg-slate-100"
                          >
                            <div className="flex items-center gap-2 text-sm font-medium leading-none text-slate-900">
                              <item.icon className="h-4 w-4 text-[#00A8E8]" />
                              {item.title}
                            </div>
                            <p className="line-clamp-2 text-sm leading-snug text-slate-500">
                              {item.desc}
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {/* Action Area */}
        <div className="flex items-center gap-4">
          <Button 
            onClick={onGetStarted}
            className="hidden md:flex bg-[#DBA328] hover:bg-[#DBA328]/90 text-black font-semibold rounded-full px-8 py-6 text-lg"
          >
            Get Started
          </Button>

          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className={mobileTriggerClasses}>
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-white/90 backdrop-blur-md border-l-white/20 p-0 text-[#052A44]">
              <div className="flex flex-col h-full py-6">
                <div className="px-6 mb-8">
                  <img src={logoPrimary} alt="Brainsie" className="h-8 w-auto" />
                </div>
                <nav className="flex-1 px-4 space-y-2">
                  <div className="px-2 py-1 text-xs font-semibold text-[#052A44]/50 uppercase tracking-wider">Services</div>
                  <Link to="/consulting" className="flex items-center gap-3 px-2 py-3 text-[#052A44] hover:bg-[#052A44]/5 rounded-lg transition-colors">
                    <BrainCircuit className="h-5 w-5 text-[#00A8E8]" />
                    Enterprise AI
                  </Link>
                  <Link to="/platform" className="flex items-center gap-3 px-2 py-3 text-[#052A44] hover:bg-[#052A44]/5 rounded-lg transition-colors">
                    <Rocket className="h-5 w-5 text-[#00A8E8]" />
                    Brainsie Platform
                  </Link>
                  <Link to="/small-business" className="flex items-center gap-3 px-2 py-3 text-[#052A44] hover:bg-[#052A44]/5 rounded-lg transition-colors">
                    <TrendingUp className="h-5 w-5 text-[#00A8E8]" />
                    Small Business
                  </Link>
                  
                  <div className="px-2 py-1 mt-6 text-xs font-semibold text-[#052A44]/50 uppercase tracking-wider">Company</div>
                  <Link to="/about" className="flex items-center gap-3 px-2 py-3 text-[#052A44] hover:bg-[#052A44]/5 rounded-lg transition-colors">
                    <Users className="h-5 w-5 text-[#00A8E8]" />
                    About Us
                  </Link>
                  <Link to="/case-studies" className="flex items-center gap-3 px-2 py-3 text-[#052A44] hover:bg-[#052A44]/5 rounded-lg transition-colors">
                    <Briefcase className="h-5 w-5 text-[#00A8E8]" />
                    Success Stories
                  </Link>
                  <Link to="/contact" className="flex items-center gap-3 px-2 py-3 text-[#052A44] hover:bg-[#052A44]/5 rounded-lg transition-colors">
                    <Phone className="h-5 w-5 text-[#00A8E8]" />
                    Contact
                  </Link>
                </nav>
                <div className="p-6 mt-auto">
                  <Button 
                    onClick={onGetStarted}
                    className="w-full bg-[#DBA328] hover:bg-[#DBA328]/90 text-black font-semibold rounded-full"
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
